import { ReactNode } from 'react'

/**
 * Small form primitives for the Flow Billing page.
 *
 * The codebase defines these inline, once per settings page — there are three
 * copies of NumberField already. These are shared across this page's four
 * cards rather than copied a fourth time, but deliberately stay local to the
 * feature rather than becoming a house component: promoting them would mean
 * reconciling three divergent existing versions, which is not this change.
 */

export function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  )
}

export function TextField({
  value,
  onChange,
  placeholder,
  maxLength,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  maxLength?: number
}) {
  return (
    <input
      type="text"
      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
      value={value}
      placeholder={placeholder}
      maxLength={maxLength}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

/**
 * `min` and `max` mirror the server-side yup bounds. Without them an
 * out-of-range value is only rejected on save, surfacing as a bare 400 toast
 * with no indication of which field was wrong.
 */
export function NumberField({
  value,
  onChange,
  min,
  max,
  step,
  allowEmpty,
}: {
  value: number | null
  onChange: (value: number | null) => void
  min?: number
  max?: number
  step?: number
  allowEmpty?: boolean
}) {
  return (
    <input
      type="number"
      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
      value={value ?? ''}
      min={min}
      max={max}
      step={step}
      onChange={(e) => {
        const raw = e.target.value
        if (raw === '') return onChange(allowEmpty ? null : 0)
        onChange(Number(raw))
      }}
    />
  )
}

export function SelectField<T extends string | number>({
  value,
  onChange,
  options,
}: {
  value: T
  onChange: (value: T) => void
  options: Array<{ value: T; label: string }>
}) {
  return (
    <select
      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
      value={String(value)}
      onChange={(e) => {
        const raw = e.target.value
        const match = options.find((o) => String(o.value) === raw)
        if (match) onChange(match.value)
      }}
    >
      {options.map((option) => (
        <option key={String(option.value)} value={String(option.value)}>
          {option.label}
        </option>
      ))}
    </select>
  )
}

export function Toggle({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
  hint?: ReactNode
}) {
  return (
    <div className="mb-4">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          className="rounded border-gray-300 h-4 w-4"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="text-sm font-medium text-gray-700">{label}</span>
      </label>
      {hint && <p className="text-xs text-gray-500 mt-1 ml-6">{hint}</p>}
    </div>
  )
}

export function Card({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">{title}</h2>
      {children}
    </div>
  )
}
